import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import DiscountRuleTable from "./DiscountRuleTable";
import DiscountFormModal from "./DiscountFormModal";
import HeaderActions from "./HeaderActions";
import FilterBar from "./FilterBar";
import LoadMore from "./LoadMore";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { PAGE_SIZE } from "./constants";
import {
  fetchDiscountRulesPage,
  createDiscountRule,
  updateDiscountRule,
  deleteDiscountRule,
} from "../../../services/discountService";

const DiscountsPage = () => {
  const [discountRules, setDiscountRules] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 350);

  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [enabled, setEnabled] = useState("");

  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadFirstPage = async () => {
    setLoading(true);
    try {
      const { data } = await fetchDiscountRulesPage({
        limit: PAGE_SIZE,
        q: debouncedQ,
        status,
        type,
        enabled,
      });
      setDiscountRules(data.items || []);
      setNextCursor(data.nextCursor || null);
      setHasMore(!!data.hasMore);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasMore || !nextCursor) return;
    setLoadingMore(true);
    try {
      const { data } = await fetchDiscountRulesPage({
        limit: PAGE_SIZE,
        cursor: nextCursor,
        q: debouncedQ,
        status,
        type,
        enabled,
      });
      setDiscountRules((prev) => prev.concat(data.items || []));
      setNextCursor(data.nextCursor || null);
      setHasMore(!!data.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, status, type, enabled]);

  const handleSave = async (formData) => {
    if (editing) await updateDiscountRule(editing._id, formData);
    else await createDiscountRule(formData);
    await loadFirstPage();
    setModalOpen(false);
    setEditing(null);
  };

  const handleDelete = async (id) => {
    await deleteDiscountRule(id);
    await loadFirstPage();
  };

  const resetFilters = () => {
    setQ("");
    setStatus("");
    setType("");
    setEnabled("");
  };

  return (
    <Box>
      <HeaderActions
        onCreate={() => setModalOpen(true)}
        onRefresh={() => {
          setRefreshing(true);
          loadFirstPage().finally(() => setRefreshing(false));
        }}
        refreshing={refreshing}
      />

      <FilterBar
        q={q}
        onQChange={setQ}
        status={status}
        onStatusChange={setStatus}
        type={type}
        onTypeChange={setType}
        enabled={enabled}
        onEnabledChange={setEnabled}
        onClearAll={resetFilters}
      />

      <DiscountRuleTable
        data={discountRules}
        loading={loading}
        onEdit={(item) => {
          setEditing(item);
          setModalOpen(true);
        }}
        onDelete={handleDelete}
      />

      <LoadMore
        visible={!loading && hasMore}
        loading={loadingMore}
        onClick={loadMore}
      />

      <DiscountFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        initialData={editing}
      />
    </Box>
  );
};

export default DiscountsPage;
